import React, { useState } from 'react';
import type { Order } from '../../types';
import { useSoftKeyboardField } from '../../hooks/useSoftKeyboardField';

interface OrderOverrideModalProps {
  order: Order;
  overrideReasons: string[];
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

const OrderOverrideModal: React.FC<OrderOverrideModalProps> = ({
  order,
  overrideReasons,
  onConfirm,
  onCancel,
}) => {
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [customReason, setCustomReason] = useState<string>('');
  const customReasonKeyboard = useSoftKeyboardField({
    id: 'override-custom-reason',
    type: 'alphanumeric',
    label: 'Custom reason',
    value: customReason,
    onChange: (v) => {
      setCustomReason(v);
      setSelectedReason('');
    },
  });

  const canConfirm = selectedReason !== '' || customReason.trim() !== '';

  const handleConfirm = () => {
    const reason = customReason.trim() || selectedReason;
    if (reason) {
      onConfirm(reason);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <header className="p-4 border-b bg-red-50">
          <h2 className="text-xl font-bold text-red-800">Override Order (Manager Only)</h2>
        </header>
        <main className="p-4">
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">Order Details:</p>
            <div className="p-3 bg-gray-50 rounded border">
              <p className="font-semibold">
                {order.orderType === 'dine-in' 
                  ? `Table: ${order.tableName || `Table ${order.tableNumber || order.tableId}`}`
                  : order.orderType === 'takeaway'
                  ? `Takeaway #${order.takeawayNumber}`
                  : `Delivery #${order.deliveryNumber}`}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Order ID: {order.id.substring(0, 8)}... | Total: ${order.totalAmount.toFixed(2)}
              </p>
            </div>
          </div>

          <div className="mb-4">
            <p className="text-sm font-semibold text-gray-700 mb-2">
              Select Override Reason: <span className="text-red-500">*</span>
            </p>
            {overrideReasons.length > 0 ? (
              <select
                value={selectedReason}
                onChange={(e) => {
                  setSelectedReason(e.target.value);
                  setCustomReason(''); // Clear custom reason when selecting from list
                }}
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">-- Select Reason --</option>
                {overrideReasons.map((reason, index) => (
                  <option key={index} value={reason}>
                    {reason}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-sm text-gray-500 italic mb-2">
                No override reasons configured. Please configure in Settings.
              </p>
            )}
          </div>

          <div className="mb-4">
            <p className="text-sm font-semibold text-gray-700 mb-2">Or Enter Custom Reason:</p>
            <input
              type="text"
              value={customReason}
              placeholder="Enter custom override reason..."
              className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-red-500 focus:border-red-500"
              data-soft-keyboard-handled
              {...customReasonKeyboard}
            />
          </div>

          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
            <p className="text-sm text-yellow-800">
              <strong>Warning:</strong> This will mark the order as comped without payment. 
              Inventory will still be deducted. This action will be logged.
            </p>
          </div>
        </main>
        <footer className="p-4 border-t flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm}
            className={`px-4 py-2 text-white rounded transition-colors ${
              canConfirm
                ? 'bg-red-500 hover:bg-red-600'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            Override Order
          </button>
        </footer>
      </div>
    </div>
  );
};

export default OrderOverrideModal;

